import axios from "axios";
import { toast } from "react-toastify";
import i18n from "src/config/i18n";
import {
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
} from "src/Funcs/auth/authStore";
import {
  refreshAccessToken,
  handleSessionExpired,
} from "src/Funcs/auth/session";
import loadingBar from "./loadingBar.jsx";
import { shouldSkipLoadingBar } from "./loadingBarExcludeList.js";
import { getAdminTabPassword, requiresAdminTab } from "src/Funcs/adminTabGate";

/** Known server error messages mapped to translation keys (server text is English-only). */
const KNOWN_SERVER_MESSAGES = [
  { pattern: /could not generate/i, key: "aiGenerateStepFailed" },
];

/** Translate known server messages, pass anything else through untouched. */
const localizeServerMessage = (message) => {
  if (!message) return message;
  const known = KNOWN_SERVER_MESSAGES.find(({ pattern }) =>
    pattern.test(message),
  );
  return known ? i18n.t(known.key, message) : message;
};

const axiosInstance = axios.create({
  // baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
});

import { applyMockInterceptor } from "src/mock/axiosMockInterceptor";
if (import.meta.env.DEV && !import.meta.env.PROD)
  applyMockInterceptor(axiosInstance);

let activeRequests = 0;

function isRefreshUrl(url) {
  if (!url) return false;
  const u = String(url);
  return u.includes("/user/refresh") || u.endsWith("user/refresh");
}

const IDEMPOTENT_METHODS = new Set(["get", "head", "options", "put", "delete"]);
const NETWORK_RETRY_MAX = 2;
const NETWORK_RETRY_BASE_MS = 500;

/**
 * How many times a request may be retried after a network-level failure
 * (no response received at all, e.g. ERR_NETWORK_CHANGED). POST/PATCH are
 * only retried when the caller opts in, because the request may have
 * reached the server before the connection dropped.
 *
 * `retryOnNetworkError` on the request config:
 *   - `false` → never retry
 *   - `true`  → retry up to NETWORK_RETRY_MAX
 *   - number  → retry up to that many times
 *   - unset   → retry idempotent methods up to NETWORK_RETRY_MAX
 */
function getNetworkRetryBudget(config) {
  if (!config || config.retryOnNetworkError === false) return 0;
  const pref = config.retryOnNetworkError;
  if (typeof pref === "number") return Math.max(0, Math.floor(pref));
  if (pref === true) return NETWORK_RETRY_MAX;
  return IDEMPOTENT_METHODS.has(String(config.method).toLowerCase())
    ? NETWORK_RETRY_MAX
    : 0;
}

const ServerToken = import.meta.env.VITE_SERVER_TOKEN;

axiosInstance.interceptors.request.use(
  async (config) => {
    const skipLoadingBar = shouldSkipLoadingBar(config.url);
    config._skipLoadingBar = skipLoadingBar;

    if (!skipLoadingBar) {
      activeRequests++;
      loadingBar.show();
    }

    if (!isRefreshUrl(config.url)) {
      let token = getAccessToken();

      // Missing or expired access token with a live refresh token → refresh
      // once via the shared single-flight helper instead of sending a request
      // that is doomed to 401.
      if ((!token || isAccessTokenExpired()) && getRefreshToken()) {
        token = await refreshAccessToken();
        config._proactiveRefreshDone = true;
      }

      if (token) {
        config.headers["x-auth-token"] = token;
      }
      if (ServerToken) {
        config.headers["x-auth-token-server"] = ServerToken;
      }

      // Password-gated admin tabs: attach the stored gate password to admin
      // requests so Users / Operations work automatically after unlock.
      if (requiresAdminTab(config.url)) {
        const pw = getAdminTabPassword();
        if (pw) config.headers["x-admin-tab-password"] = pw;
      }
    } else if (ServerToken) {
      config.headers["x-auth-token-server"] = ServerToken;
    }

    return config;
  },
  (error) => {
    if (error.config?._skipLoadingBar) {
      return Promise.reject(error);
    }
    activeRequests = Math.max(activeRequests - 1, 0);
    loadingBar.hide();
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (!response.config?._skipLoadingBar) {
      activeRequests = Math.max(activeRequests - 1, 0);
      loadingBar.hide();
    }
    return response;
  },
  async (error) => {
    if (!error.config?._skipLoadingBar) {
      activeRequests = Math.max(activeRequests - 1, 0);
      loadingBar.hide();
    }

    if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest) {
      if (isRefreshUrl(originalRequest.url)) {
        // The refresh call itself was rejected → the session is dead.
        handleSessionExpired();
        return Promise.reject(error);
      }

      const canRefresh = Boolean(getRefreshToken());
      const alreadyHandled =
        originalRequest._retry || originalRequest._proactiveRefreshDone;

      if (!alreadyHandled && canRefresh) {
        originalRequest._retry = true;
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers["x-auth-token"] = newAccessToken;
          return axiosInstance(originalRequest);
        }
        // Refresh token rejected → session is dead.
        handleSessionExpired();
        return Promise.reject(error);
      }

      // A session existed at request time but there is nothing left to
      // recover it with (no refresh token) → dead session.
      const hadSession = Boolean(
        getAccessToken() ||
        originalRequest._proactiveRefreshDone ||
        originalRequest.headers?.["x-auth-token"],
      );
      if (!canRefresh && hadSession) {
        handleSessionExpired();
        return Promise.reject(error);
      }

      // Otherwise: anonymous traffic (e.g. member-only endpoints hit from a
      // public board view) or a permission denial after a successful retry —
      // not a session problem, fall through to the generic error handling.
    }

    if (error.response) {
      const { status, data } = error.response;
      const method = error.config?.method?.toUpperCase();
      const url = error.config?.url ?? error.config?.baseURL ?? "unknown";
      const responseData = error.config?.sensitiveRequest
        ? "[redacted sensitive response]"
        : data;
      const fullMessage = [
        status,
        method,
        url,
        responseData && typeof responseData === "object"
          ? JSON.stringify(responseData)
          : responseData,
      ]
        .filter(Boolean)
        .join(" | ");
      console.error("[API Error]", fullMessage);
      console.error("[API Error details]", {
        status,
        method,
        url,
        requestData: error.config?.sensitiveRequest
          ? "[redacted sensitive request]"
          : error.config?.data,
        responseData,
      });

      if (status === 429) {
        toast.error("Rate limit exceeded. Redirecting...");

        setTimeout(() => {
          if (typeof window !== "undefined") {
            window.location.href = "/rate-limit-error";
          }
        }, 2000);

        return Promise.reject(new Error("Rate limit exceeded, redirected."));
      }

      // `skipErrorToast` may be `true` (skip all) or a predicate
      // `(data, error) => boolean` to skip only specific errors.
      const skipFlag = error.config?.skipErrorToast;
      let shouldSkipToast =
        typeof skipFlag === "function"
          ? skipFlag(data, error)
          : Boolean(skipFlag);

      // Sessionless 401s are never actionable for the viewer (anonymous
      // public-board traffic, login-page calls) — don't toast them.
      if (status === 401 && !getAccessToken() && !getRefreshToken()) {
        shouldSkipToast = true;
      }

      if (!shouldSkipToast) {
        if (
          status !== 200 &&
          data?.message &&
          !error.config?.sensitiveRequest
        ) {
          toast.error(localizeServerMessage(data.message));
        } else if (status === 404) {
          toast.error(`Not found (404): ${method} ${url}`);
        }
      }
    } else {
      const retryBudget = getNetworkRetryBudget(originalRequest);
      const attempts = originalRequest?._networkRetryCount ?? 0;

      if (originalRequest && attempts < retryBudget) {
        originalRequest._networkRetryCount = attempts + 1;
        const backoffMs =
          NETWORK_RETRY_BASE_MS * (attempts + 1) + Math.random() * 250;
        console.warn(
          `[API Retry] ${originalRequest.method?.toUpperCase()} ${originalRequest.url} ` +
            `failed (${error.message}) — retry ${attempts + 1}/${retryBudget} in ${Math.round(backoffMs)}ms`,
        );
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
        return axiosInstance(originalRequest);
      }

      console.error("[API Error] No response", {
        message: error.message,
        config: error.config
          ? { url: error.config.url, method: error.config.method }
          : null,
      });
      const skipFlag = error.config?.skipErrorToast;
      const shouldSkipToast =
        typeof skipFlag === "function"
          ? skipFlag(null, error)
          : Boolean(skipFlag);
      if (!shouldSkipToast) {
        toast.error(error.message || "An unexpected error occurred.");
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
